import {
  useAiProcessingSummary,
  useAiProcessingWidget,
  useDismissAiProcessingItem,
} from '@hooks/useAiProcessingStats';
import { useBulkFileUpload } from '@hooks/useBulkFileUpload';
import { useToast } from '@retracedgmbh/ui-components-lib';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@/hooks/useApi';
import * as api from '@/services/api';
import AIProcessingSheet from './AIProcessingSheet';
import { AIProcessingStat } from './AIProcessingStat';
import AssignDocumentDialog, {
  type AssignDocumentData,
} from './AssignDocumentDialog';
import type { AiProcessingItem } from './types';

export default function AIProcessingWidget() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [assignDialogItem, setAssignDialogItem] =
    useState<AiProcessingItem | null>(null);
  const [workflowMetadata, setWorkflowMetadata] = useState<Record<
    string,
    unknown
  > | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const dismissMutation = useDismissAiProcessingItem();

  const { isVisible, statsData } = useAiProcessingWidget();
  // The sheet dropped its sort filter (two fixed sections now), so the summary
  // is always fetched unsorted.
  const { data: summaryData, isLoading: summaryLoading } =
    useAiProcessingSummary();
  const bulkUpload = useBulkFileUpload();

  // Resolved stats + items for the navbar button/popover. Summary (which also
  // carries the per-job items) first, /stats endpoint as fallback.
  const stats = summaryData?.stats ?? statsData;
  const items = summaryData?.items ?? [];
  const classifiedCount =
    bulkUpload.classifyResults?.results.filter(r => r.status === 'completed')
      .length ?? 0;

  const assignMutation = useMutation({
    mutationFn: (data: AssignDocumentData) =>
      api.companyDocumentBulkCreate({
        companyId: data.companyId,
        documents: [
          {
            name: data.fileName,
            documentTypeId: data.documentTypeId,
            fileId: data.fileId,
            isPublic: false,
          },
        ],
      }),
  });

  const handleCta = async (item: AiProcessingItem) => {
    if (item.assetType === 'COMPANY_DOCUMENTS' && item.status === 'COMPLETED') {
      try {
        const res = await api.aiProcessing.getCompanyDocumentProcess(
          item.metadata.workflowId,
        );
        setWorkflowMetadata(res.data.data.metadata);
      } catch {
        setWorkflowMetadata(null);
      }
      setAssignDialogItem(item);
      setSheetOpen(false);
      return;
    }
    if (item.metadata.ctaUrl) {
      setSheetOpen(false);
      navigate(item.metadata.ctaUrl);
    }
  };

  const handleAssignSubmit = async (data: AssignDocumentData) => {
    if (!assignDialogItem) return;
    try {
      await assignMutation.mutateAsync(data);
      toast({ title: 'Document shared successfully', variant: 'success' });
      await dismissMutation.mutateAsync(assignDialogItem.id);
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
      setAssignDialogItem(null);
    } catch (error) {
      toast({
        title: 'Failed to share document',
        description: (error as Error).message,
        variant: 'destructive',
      });
    }
  };

  const handleAssignAndNavigate = async (data: AssignDocumentData) => {
    if (!assignDialogItem) return;
    try {
      await assignMutation.mutateAsync(data);
      toast({ title: 'Document shared successfully', variant: 'success' });
      await dismissMutation.mutateAsync(assignDialogItem.id);
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
      setAssignDialogItem(null);
      setSheetOpen(false);
      navigate('/company-documents/list');
    } catch (error) {
      toast({
        title: 'Failed to share document',
        description: (error as Error).message,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      {isVisible && (
        <AIProcessingStat
          onOpen={() => setSheetOpen(true)}
          stats={stats}
          items={items}
          classifiedCount={classifiedCount}
        />
      )}

      <AssignDocumentDialog
        open={!!assignDialogItem}
        onOpenChange={open => {
          if (!open) {
            setAssignDialogItem(null);
            setWorkflowMetadata(null);
          }
        }}
        fileName={
          assignDialogItem?.assetName ||
          assignDialogItem?.metadata?.file?.name ||
          ''
        }
        fileId={assignDialogItem?.metadata?.fileId || ''}
        workflowMetadata={workflowMetadata}
        onSubmit={handleAssignSubmit}
        onGoToDocument={handleAssignAndNavigate}
        isSubmitting={assignMutation.isPending}
      />

      <AIProcessingSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        data={summaryData}
        isLoading={summaryLoading}
        onCta={handleCta}
        bulkUploadBatchKey={bulkUpload.batchKey}
        bulkUploadFiles={bulkUpload.uploadedFiles}
        onBulkUploadFinish={bulkUpload.handleUploadFinish}
        isBulkClassifying={bulkUpload.isClassifying}
        classifyResults={bulkUpload.classifyResults?.results}
        typeOverrides={bulkUpload.typeOverrides}
        onTypeChange={bulkUpload.handleTypeChange}
        onAccept={bulkUpload.handleAccept}
        isAccepting={bulkUpload.isAccepting}
        onCancel={bulkUpload.resetBatch}
        isPolling={bulkUpload.isPolling}
      />
    </>
  );
}
